import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { Card, CardBody, CardFooter } from "reactstrap";
import PropTypes from "prop-types";
import classnames from "classnames";
import {
  Stepper as MuiStepper,
  Step,
  Button,
  useMediaQuery,
  StepButton,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { isEmpty } from "lodash";

import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";

import colors from "assets/scss/colors.module.scss";

const Connector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.primary.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.primary.main,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.mode === "dark" ? colors.grey : colors.lightgrey,
    borderTopWidth: 3,
    borderRadius: 1,
  },
}));

const Stepper = ({
  steps,
  handleSubmit = () => {},
  handleDraft = () => {},
  activeStepIndex = 0,
  setActiveStepIndex = () => {},
  isLoading,
  validateDocument = () => {},
  formik,
  isDraftDisabled = false,
  t,
  ...props
}) => {
  const [isDraft, setIsDraft] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setIsDraft(false);
    }
  }, [isLoading]);

  const isMobileDevice = useMediaQuery("(max-width: 768px)");

  const handleStepChange = async (isNext = false) => {
    if (isNext) {
      handleSubmit();
      validateDocument();
    } else {
      setActiveStepIndex((prev) => prev - 1);
    }
  };

  const goToStep = (isCompleted, index) => {
    // validating form details while navigating from navigation bar
    if (isCompleted) {
      const currentKeys = !isEmpty(formik?.errors)
        ? Object.keys(formik?.errors)
        : [];
      const currentFormError = currentKeys?.length
        ? Object?.keys(Object?.values(formik?.errors)?.[0])
        : [];
      if (isEmpty(formik.errors) || index <= activeStepIndex) {
        setActiveStepIndex(index);
      }
      currentFormError?.forEach((key) =>
        formik.setFieldTouched(`${currentKeys?.[0]}.${key}`, true)
      );
    }
  };

  const currentStep = useMemo(
    () => steps[activeStepIndex],
    [activeStepIndex, steps]
  );

  return (
    <Card className="m-0 h-100 shadow-none" {...props}>
      <MuiStepper
        activeStep={activeStepIndex}
        alternativeLabel
        sx={{
          marginTop: "20px",
          marginBottom: 0,
        }}
        className={classnames({
          "mb-4": !isMobileDevice,
          "mb-2": isMobileDevice,
        })}
        connector={<Connector />}
      >
        {steps.map((step, index) => (
          <Step
            key={step.id}
            onClick={() => goToStep(step.isCompleted, index)}
            completed={step.isCompleted}
          >
            <StepButton>
              <span
                className={`${currentStep.id === step.id ? "fw-bold" : ""}`}
              >
                {!isMobileDevice ? t(`${step.name}`) : ""}
              </span>
            </StepButton>
          </Step>
        ))}
      </MuiStepper>
      <CardBody className="overflow-auto">
        {isMobileDevice && (
          <p className="text-center fs-3">{t(currentStep.name)}</p>
        )}
        <div className="stepper-main-wrapper">{currentStep.component}</div>
      </CardBody>

      <CardFooter className="d-flex justify-content-between">
        <Button
          variant="outlined"
          size={`${isMobileDevice ? "small" : "medium"}`}
          sx={{ minWidth: "unset" }}
          onClick={() => handleStepChange()}
          disabled={!activeStepIndex}
        >
          {isMobileDevice ? "<" : t("Previous")}
        </Button>

        <div className="d-flex gap-2">
          {!isDraftDisabled && (
            <Button
              variant="contained"
              size={`${isMobileDevice ? "small" : "medium"}`}
              sx={{ minWidth: "unset" }}
              onClick={() => {
                setIsDraft(true);
                handleDraft();
              }}
              disabled={isLoading && isDraft}
            >
              {t("Save Info")}
              {isLoading && isDraft && <CircularProgress size={20} />}
            </Button>
          )}
          {activeStepIndex === steps.length - 1 ? (
            <Button
              variant="contained"
              size={`${isMobileDevice ? "small" : "medium"}`}
              className="d-flex gap-2"
              sx={{ minWidth: "unset" }}
              onClick={handleSubmit}
              disabled={isLoading && !isDraft}
            >
              {t("Submit")}
              {isLoading && !isDraft && <CircularProgress size={20} />}
            </Button>
          ) : (
            <Button
              color="primary"
              size={`${isMobileDevice ? "small" : "medium"}`}
              sx={{ minWidth: "unset" }}
              variant="outlined"
              disabled={isLoading && !isDraft}
              onClick={() => handleStepChange(true)}
            >
              {t("Next")}
              {isLoading && !isDraft && <CircularProgress size={20} />}
            </Button>
          )}
        </div>
      </CardFooter>
    </Card>
  );
};

Stepper.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      isCompleted: PropTypes.bool,
      component: PropTypes.element,
    }).isRequired
  ),
  handleSubmit: PropTypes.func,
  handleDraft: PropTypes.func,
  activeStepIndex: PropTypes.number,
  validateDocument: PropTypes.func,
  setActiveStepIndex: PropTypes.func,
  isLoading: PropTypes.bool,
  isDraftDisabled: PropTypes.bool,
  formik: PropTypes.object,
  t: PropTypes.func,
};

export default withTranslation()(Stepper);
